import React, { useState, useRef, useEffect } from 'react';
import { Message as MessageType } from '../../types/kyb';
import Message from './Message';
import TypingIndicator from './TypingIndicator';

interface ChatInterfaceProps {
  messages: MessageType[];
  isAITyping?: boolean;
  onSendMessage: (message: string) => void;
  onSuggestionClick: (suggestion: string) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isAITyping = false,
  onSendMessage,
  onSuggestionClick
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAITyping]);

  const handleSendMessage = () => {
    if (inputValue.trim()) {
      onSendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8 flex items-center">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-secondary-500 to-primary-500 shadow-sm flex items-center justify-center text-white mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-secondary-500">
              KYB Verification Assistant
            </h1>
          </div>

          {/* Message list */}
          <div className="space-y-2">
            {messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                onSuggestionClick={onSuggestionClick}
              />
            ))}

            {/* Typing indicator */}
            {isAITyping && (
              <div className="ml-12 mt-2">
                <TypingIndicator />
              </div>
            )}

            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Message input */}
      <div className="border-t border-neutral-200 bg-white p-5">
        <div className="max-w-3xl mx-auto flex">
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about KYB requirements or next steps..."
              className="w-full border border-neutral-300 bg-white rounded-xl pl-4 pr-12 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 shadow-sm hover:border-neutral-400 transition-colors"
            />
            <div className="absolute right-2 top-2 flex items-center space-x-1">
              <button 
                className="p-1.5 rounded-full text-neutral-400 hover:text-primary-500 hover:bg-neutral-100 transition-colors"
                aria-label="Attach file"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
              </button>
            </div>
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
            className={`ml-3 ${
              inputValue.trim() 
                ? 'bg-primary-500 hover:bg-primary-600 shadow-button hover:shadow-button-hover' 
                : 'bg-neutral-200 cursor-not-allowed'
            } text-white rounded-xl px-4 flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
            aria-label="Send message"
          >
            <span className="mr-1.5 font-medium">Send</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;